<script setup>
// ============================================================================
// BonBoZ: bảng "Thống kê chi tiết" của trang Nhân viên — đủ 10 cột Pancake,
// hai tab [Nhân viên] [Thời gian] + nút [Tuỳ chỉnh] và bộ lọc tài khoản.
//
// CÙNG KHUÔN với `BangTheoNgay.vue` của trang Trang: cùng kiểu tab, cùng chọn
// cột nhớ trong localStorage, cùng dòng Tổng có đơn vị. Hai bảng đứng ở hai
// trang nhưng người dùng đọc chúng trong cùng một buổi — khác khuôn là bắt học
// hai lần.
//
// "T.LỆ PH.HỒI" TÍNH TẠI CHỖ, không phải cột máy chủ trả về: nó là phần chia
// của mỗi người trên TỔNG CỘT (tin nhắn + bình luận của tất cả mọi người), nên
// giá trị phụ thuộc vào những ai đang có mặt trong bảng. Lọc bớt người thì tỉ
// lệ phải tính lại — để máy chủ trả sẵn là con số đứng im trong khi bảng đổi.
//
// DÒNG TỔNG KHÔNG PHẢI TỔNG CỘT ở hai cột "SĐT mang về" và "Số đơn chốt": một
// hội thoại hai người cùng chăm thì cả hai dòng đều được tính, còn dòng Tổng
// đếm distinct trên trọn kỳ. Chú giải nói thẳng điều đó ngay tại ô.
// ============================================================================
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ngay } from 'dashboard/helper/dinhDangNgay';
import { dinhDangThoiLuong } from 'dashboard/helper/thoiLuong';
import {
  taiXuongExcel,
  tenFileThongKe,
} from 'dashboard/helper/bonboz/xuatExcel';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  dong: { type: Array, default: () => [] },
  tong: { type: Object, default: null },
  gom: { type: String, default: 'nhan_vien' },
  dangTai: { type: Boolean, default: false },
});
const emit = defineEmits(['doiGom']);

const { t } = useI18n();

// `kieu` quyết định cách vẽ ô: số trơn, khoảng thời gian, hay phần trăm.
const COT = [
  { khoa: 'tin_tu_binh_luan', don_vi: 'DV_TIN' },
  { khoa: 'binh_luan', don_vi: 'DV_BINH_LUAN' },
  { khoa: 'phien_bl', don_vi: 'DV_PHIEN' },
  { khoa: 'tin_nhan', don_vi: 'DV_TIN' },
  { khoa: 'phien_tin', don_vi: 'DV_PHIEN' },
  { khoa: 'phan_hoi_tb', don_vi: 'DV_THOI_GIAN', kieu: 'thoi_gian' },
  { khoa: 'sdt_mang_ve', don_vi: 'DV_SDT' },
  { khoa: 'don_chot', don_vi: 'DV_DON' },
  { khoa: 'ti_le', don_vi: 'DV_PHAN_TRAM', kieu: 'ti_le' },
];

// Hai cột đếm KHÁCH: dòng Tổng nhỏ hơn tổng cột là đúng — xem đầu file.
const DEM_KHACH = ['sdt_mang_ve', 'don_chot'];

// ------------------------------------------------------------- chọn cột
// Lưu trong localStorage, cùng lý do với bảng của trang Trang: người trực page
// mở bảng này mỗi ngày, bắt bỏ chọn lại mỗi lần là bắt trả giá cho việc thường
// xuyên nhất. Hỏng hay bị chặn thì rơi về "hiện tất cả".
const KHOA_LUU = 'bonboz_cot_thong_ke_nhan_vien';
const doc = () => {
  try {
    const luu = JSON.parse(localStorage.getItem(KHOA_LUU));
    return Array.isArray(luu) && luu.length ? luu : null;
  } catch {
    return null;
  }
};
const cotHien = ref(doc() || COT.map(c => c.khoa));
watch(cotHien, giaTri => {
  try {
    localStorage.setItem(KHOA_LUU, JSON.stringify(giaTri));
  } catch {
    // Trình duyệt chặn lưu trữ thì bảng vẫn chạy, chỉ là mở lại phải chọn lại.
  }
});
const moChonCot = ref(false);
const cot = computed(() => COT.filter(c => cotHien.value.includes(c.khoa)));

const doiCot = khoa => {
  const co = cotHien.value.includes(khoa);
  // Không cho bỏ hết: bảng còn mỗi cột tên thì không còn là bảng.
  if (co && cotHien.value.length === 1) return;
  cotHien.value = co
    ? cotHien.value.filter(k => k !== khoa)
    : COT.map(c => c.khoa).filter(k => cotHien.value.includes(k) || k === khoa);
};

// ------------------------------------------------------- lọc theo tài khoản
const locNguoi = ref(null); // null = tất cả tài khoản
const moLoc = ref(false);
const danhSachNguoi = computed(() =>
  props.gom === 'nhan_vien'
    ? props.dong.map(d => ({ id: d.id, ten: d.ten, anh: d.anh }))
    : []
);
// Đổi tab thì bỏ lọc: tab thời gian không có người để lọc, giữ lại là giữ một
// bộ lọc vô hình vẫn đang chạy.
watch(
  () => props.gom,
  () => {
    locNguoi.value = null;
  }
);

// -------------------------------------------------------------- giá trị ô
// Mẫu số là TỔNG CỘT của những dòng đang hiện — xem đầu file.
const tongPhanHoi = computed(() =>
  props.dong.reduce((s, d) => s + d.tin_nhan + d.binh_luan, 0)
);

const oCua = (nguon, c) => {
  if (!nguon) return null;
  if (c.kieu === 'thoi_gian') {
    // `null` (chưa ai trả lời) khác hẳn 0 giây. Gộp lại thì một ngày không ai
    // trực đọc thành trả lời tức thì.
    return nguon.phan_hoi_tb === null || nguon.phan_hoi_tb === undefined
      ? null
      : dinhDangThoiLuong(nguon.phan_hoi_tb);
  }
  if (c.kieu === 'ti_le') {
    if (!tongPhanHoi.value) return null;
    const phan = ((nguon.tin_nhan + nguon.binh_luan) / tongPhanHoi.value) * 100;
    return `${Math.round(phan * 100) / 100}%`;
  }
  return nguon[c.khoa] ?? 0;
};

const hang = computed(() => {
  const ds =
    props.gom === 'gio' || props.gom === 'thoi_gian'
      ? // Thời gian: mới nhất lên đầu, cùng quy ước với bảng của trang Trang.
        [...props.dong].sort((a, b) => b.moc - a.moc)
      : props.dong.filter(d => !locNguoi.value || d.id === locNguoi.value);
  return ds.map(d => ({
    ...d,
    nhan: props.gom === 'thoi_gian' ? ngay(d.moc * 1000, 'ngayDay') : d.ten,
  }));
});

// Dòng Tổng của tab nhân viên phải theo BỘ LỌC: lọc còn một người mà dòng Tổng
// vẫn là số cả phòng thì hai dòng cạnh nhau nói hai chuyện.
const tongHien = computed(() => {
  if (props.gom === 'nhan_vien' && locNguoi.value) {
    return props.dong.find(d => d.id === locNguoi.value) || null;
  }
  return props.tong;
});

// ------------------------------------------------------------------ Excel
// Xuất ĐÚNG cái bảng đang hiện — cùng cột, cùng thứ tự dòng, cùng dòng Tổng.
const xuatExcel = () => {
  const cacHang = [
    [
      t(
        `BONBOZ.NHAN_VIEN.COT_${props.gom === 'thoi_gian' ? 'THOI_GIAN' : 'TEN'}`
      ),
      ...cot.value.map(c => t(`BONBOZ.NHAN_VIEN.C_${c.khoa}`)),
    ],
    ...hang.value.map(d => [d.nhan, ...cot.value.map(c => oCua(d, c) ?? '')]),
  ];
  if (tongHien.value) {
    cacHang.push([
      t('BONBOZ.TRANG_TK.COT_TONG'),
      ...cot.value.map(c => oCua(tongHien.value, c) ?? ''),
    ]);
  }
  const moc = props.gom === 'thoi_gian' ? props.dong.map(d => d.moc) : [];
  taiXuongExcel(
    tenFileThongKe(
      `thong-ke-nhan-vien-${props.gom}`,
      moc.length ? Math.min(...moc) : null,
      moc.length ? Math.max(...moc) : null
    ),
    cacHang
  );
};

const TAB = [
  { khoa: 'nhan_vien', nhan: 'TAB_NHAN_VIEN' },
  { khoa: 'thoi_gian', nhan: 'TAB_THOI_GIAN' },
];
</script>

<template>
  <section class="border rounded-xl border-n-weak bg-n-solid-2">
    <div class="p-4 pb-0">
      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ $t('BONBOZ.NHAN_VIEN.CHI_TIET') }}
      </h3>
      <p class="m-0 mt-0.5 text-xs text-n-slate-11">
        {{ $t('BONBOZ.NHAN_VIEN.CHI_TIET_MO_TA') }}
      </p>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3 px-4 pt-3">
      <!-- Tab gạch chân, đúng khuôn Pancake ở trang này (khác trang Trang: bên
           đó Pancake dùng tab viên thuốc). Giữ đúng từng trang một. -->
      <div class="flex gap-5 -mb-px">
        <button
          v-for="x in TAB"
          :key="x.khoa"
          type="button"
          class="pb-2 text-sm transition-colors border-b-2"
          :class="
            gom === x.khoa
              ? 'border-n-brand text-n-brand font-medium'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="emit('doiGom', x.khoa)"
        >
          {{ $t(`BONBOZ.NHAN_VIEN.${x.nhan}`) }}
        </button>
      </div>

      <div class="flex items-center gap-2 pb-2">
        <div class="relative">
          <Button
            variant="outline"
            color="slate"
            size="sm"
            icon="i-lucide-columns-3"
            :label="$t('BONBOZ.TRANG_TK.TUY_CHINH')"
            @click="moChonCot = !moChonCot"
          />
          <!-- Nền trong suốt phủ toàn màn: bấm ra ngoài là đóng, không cần
               nghe sự kiện trên document rồi phải nhớ gỡ. -->
          <div
            v-if="moChonCot"
            class="fixed inset-0 z-10"
            @click="moChonCot = false"
          />
          <div
            v-if="moChonCot"
            class="absolute right-0 z-20 flex flex-col gap-1 p-2 mt-1 overflow-y-auto border shadow-lg w-60 max-h-80 rounded-xl border-n-weak bg-n-solid-1"
          >
            <label
              v-for="c in COT"
              :key="c.khoa"
              class="flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
            >
              <input
                type="checkbox"
                :checked="cotHien.includes(c.khoa)"
                class="accent-n-brand"
                @change="doiCot(c.khoa)"
              />
              {{ $t(`BONBOZ.NHAN_VIEN.C_${c.khoa}`) }}
            </label>
          </div>
        </div>

        <!-- Bộ lọc tài khoản chỉ có nghĩa ở tab Nhân viên; tab Thời gian gộp
             mọi người nên không có gì để lọc. -->
        <div v-if="gom === 'nhan_vien'" class="relative">
          <Button
            variant="outline"
            color="slate"
            size="sm"
            icon="i-lucide-users"
            trailing-icon="i-lucide-chevron-down"
            :label="
              locNguoi
                ? danhSachNguoi.find(n => n.id === locNguoi)?.ten
                : $t('BONBOZ.NHAN_VIEN.TAT_CA_TAI_KHOAN')
            "
            @click="moLoc = !moLoc"
          />
          <div v-if="moLoc" class="fixed inset-0 z-10" @click="moLoc = false" />
          <div
            v-if="moLoc"
            class="absolute right-0 z-20 flex flex-col gap-1 p-2 mt-1 overflow-y-auto border shadow-lg w-60 max-h-80 rounded-xl border-n-weak bg-n-solid-1"
          >
            <button
              type="button"
              class="px-2 py-1.5 text-sm text-left rounded text-n-slate-12 hover:bg-n-alpha-1"
              @click="
                locNguoi = null;
                moLoc = false;
              "
            >
              {{ $t('BONBOZ.NHAN_VIEN.TAT_CA_TAI_KHOAN') }}
            </button>
            <button
              v-for="n in danhSachNguoi"
              :key="n.id"
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded text-n-slate-12 hover:bg-n-alpha-1"
              @click="
                locNguoi = n.id;
                moLoc = false;
              "
            >
              <Avatar :src="n.anh" :name="n.ten" :size="20" rounded-full />
              <span class="truncate">{{ n.ten }}</span>
            </button>
          </div>
        </div>

        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-download"
          :label="$t('BONBOZ.XUAT.NUT')"
          :disabled="!hang.length"
          @click="xuatExcel"
        />
      </div>
    </div>

    <div
      v-if="dangTai"
      class="m-4 rounded-lg h-72 bg-n-alpha-2 animate-pulse"
    />

    <!-- Bảng tự cuộn cả hai chiều trong khung của nó; trang không bao giờ cuộn
         ngang. Đầu bảng và cột tên dính lại khi cuộn — mất tên cột thì mỗi con
         số là một câu đố. -->
    <div v-else class="overflow-auto max-h-[28rem] border-t border-n-weak">
      <!-- BonBoZ: boc de cuon ngang. Bang nhieu cot hon be ngang man thi bi CAT
           cung chu khong cuon — tren dien thoai mat han cac cot ben phai. Hai man
           Cam xuc va Quang cao da boc dung tu truoc, day la lam not cho deu. -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead class="sticky top-0 z-10 bg-n-slate-2">
            <tr class="text-xs border-b border-n-weak text-n-slate-11">
              <th
                class="sticky left-0 z-10 w-48 px-3 py-2.5 font-medium bg-n-slate-2 text-start"
              >
                {{
                  $t(
                    `BONBOZ.NHAN_VIEN.COT_${gom === 'thoi_gian' ? 'THOI_GIAN' : 'TEN'}`
                  )
                }}
              </th>
              <th
                v-for="c in cot"
                :key="c.khoa"
                v-tooltip.top="$t(`BONBOZ.NHAN_VIEN.G_${c.khoa}`)"
                class="px-3 py-2.5 font-medium leading-snug align-bottom whitespace-nowrap text-end"
              >
                {{ $t(`BONBOZ.NHAN_VIEN.C_${c.khoa}`) }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="d in hang"
              :key="d.id ?? d.moc"
              class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
            >
              <td
                class="sticky left-0 px-3 py-2 whitespace-nowrap bg-n-solid-2 text-n-slate-12"
              >
                <span v-if="gom === 'thoi_gian'">{{ d.nhan }}</span>
                <span v-else class="flex items-center gap-2">
                  <Avatar :src="d.anh" :name="d.ten" :size="28" rounded-full />
                  <span class="truncate">{{ d.ten }}</span>
                </span>
              </td>
              <td
                v-for="c in cot"
                :key="c.khoa"
                v-tooltip.top="
                  c.kieu === 'thoi_gian' && d.phan_hoi_tv !== null
                    ? $t('BONBOZ.NHAN_VIEN.GT_TRUNG_VI', {
                        tv: dinhDangThoiLuong(d.phan_hoi_tv),
                      })
                    : undefined
                "
                class="px-3 py-2 tabular-nums text-end"
                :class="oCua(d, c) ? 'text-n-slate-12' : 'text-n-slate-10'"
              >
                {{ oCua(d, c) ?? '—' }}
              </td>
            </tr>
          </tbody>

          <tfoot v-if="tongHien">
            <tr
              class="sticky bottom-0 text-sm border-t bg-n-slate-2 border-n-weak text-n-slate-12"
            >
              <td class="sticky left-0 px-3 py-2.5 font-semibold bg-n-slate-2">
                {{ $t('BONBOZ.TRANG_TK.COT_TONG') }}
              </td>
              <td
                v-for="c in cot"
                :key="c.khoa"
                v-tooltip.top="
                  DEM_KHACH.includes(c.khoa)
                    ? $t('BONBOZ.NHAN_VIEN.GT_TONG_KHACH')
                    : undefined
                "
                class="px-3 py-2 text-end"
              >
                <span
                  class="block font-semibold tabular-nums"
                  :class="
                    DEM_KHACH.includes(c.khoa)
                      ? 'underline decoration-dotted decoration-n-slate-8 underline-offset-4'
                      : ''
                  "
                >
                  {{ oCua(tongHien, c) ?? '—' }}
                </span>
                <span class="block text-xxs text-n-slate-11">
                  {{ $t(`BONBOZ.TRANG_TK.${c.don_vi}`) }}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <p class="px-4 py-2.5 m-0 text-xxs text-n-slate-11">
      {{ $t('BONBOZ.NHAN_VIEN.GT_TONG_KHACH') }}
    </p>
  </section>
</template>
